import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Link2, Fingerprint, Clock, PartyPopper } from "lucide-react";
import { getLinkedDevices, disconnectDevice, unassignDevice, blockDeviceAssignment, grantDeviceLocationException } from "../../services/linkedDeviceService";
import Loader from "../../components/common/Loader";
import Button from "../../components/common/Button";
import Card from "../../components/common/Card";
import Badge from "../../components/common/Badge";
import EmptyState from "../../components/common/EmptyState";
import ConfirmModal from "../../components/common/ConfirmModal";
import Modal from "../../components/common/Modal";
import LinkDeviceModal from "../../components/common/LinkDeviceModal";
import AssignDeviceModal from "../../components/common/AssignDeviceModal";
import { toast } from "react-toastify";
import { color } from "../../styles/theme";
import { useAuth } from "../../context/AuthContext";

const formatDateTime = (value, t) => {
  if (!value) return t("never");
  return new Date(value).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const LinkedDevices = () => {
  const { t } = useTranslation(["linkedDevices", "common"]);
  const { subAccountContext } = useAuth();
  const canManageLocationExceptions = !subAccountContext?.IsSubAccount || Boolean(subAccountContext?.Permissions?.CanManageLocationExceptions);
  const QR_TYPE_LABELS = { eQR: t("qrTypes.eQR"), pQR: t("qrTypes.pQR"), lQR: t("qrTypes.lQR") };
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [disconnectTarget, setDisconnectTarget] = useState(null);
  const [blockTarget, setBlockTarget] = useState(null);
  const [blockReason, setBlockReason] = useState("");
  const [exceptionTarget, setExceptionTarget] = useState(null);
  const [exceptionReason, setExceptionReason] = useState("");
  const [exceptionDuration, setExceptionDuration] = useState("60");
  const [exceptionConfirmed, setExceptionConfirmed] = useState(false);
  const [busyDeviceId, setBusyDeviceId] = useState(null);

  const loadDevices = () => {
    setLoading(true);
    return getLinkedDevices()
      .then((res) => setDevices(res.data?.Devices || []))
      .catch(() => toast.error(t("toast.loadFailed")))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadDevices();
  }, []);

  const handleUnassign = async (device) => {
    setBusyDeviceId(device.Id);
    try {
      const res = await unassignDevice(device.Id);
      if (res.data?.Success) {
        toast.success(res.data?.Message || t("toast.unassignSuccess"));
        loadDevices();
      } else {
        toast.error(res.data?.Message || t("toast.unassignFailed"));
      }
    } catch (err) {
      toast.error(err.response?.data?.Message || t("toast.unassignFailed"));
    } finally {
      setBusyDeviceId(null);
    }
  };

  const handleDisconnect = async () => {
    if (!disconnectTarget) return;
    setBusyDeviceId(disconnectTarget.Id);
    try {
      const res = await disconnectDevice(disconnectTarget.Id);
      if (res.data?.Success) {
        toast.success(res.data?.Message || t("toast.disconnectSuccess"));
        loadDevices();
      } else {
        toast.error(res.data?.Message || t("toast.disconnectFailed"));
      }
    } catch (err) {
      toast.error(err.response?.data?.Message || t("toast.disconnectFailed"));
    } finally {
      setBusyDeviceId(null);
      setDisconnectTarget(null);
    }
  };

  const handleBlock = async () => {
    if (!blockTarget || !blockReason.trim()) return;
    setBusyDeviceId(blockTarget.Id);
    try {
      const res = await blockDeviceAssignment(blockTarget.Id, blockReason.trim());
      if (res.data?.Success) {
        toast.success(res.data?.Message || t("toast.blockSuccess"));
        await loadDevices();
        setBlockTarget(null);
        setBlockReason("");
      } else {
        toast.error(res.data?.Message || t("toast.blockFailed"));
      }
    } catch (err) {
      toast.error(err.response?.data?.Message || t("toast.blockFailed"));
    } finally {
      setBusyDeviceId(null);
    }
  };

  const handleLocationException = async () => {
    if (!exceptionTarget || !exceptionReason.trim() || !exceptionConfirmed) return;
    setBusyDeviceId(exceptionTarget.Id);
    try {
      const res = await grantDeviceLocationException(exceptionTarget.Id, Number(exceptionDuration), exceptionReason.trim(), exceptionConfirmed);
      if (res.data?.Success) {
        toast.success(res.data?.Message || t("toast.exceptionSuccess"));
        await loadDevices();
        setExceptionTarget(null);
      } else toast.error(res.data?.Message || t("toast.exceptionFailed"));
    } catch (err) {
      toast.error(err.response?.data?.Message || t("toast.exceptionFailed"));
    } finally {
      setBusyDeviceId(null);
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      <div style={s.headerRow}>
        <h2 style={s.heading}>
          <Link2 size={19} strokeWidth={2.25} /> {t("heading")}
          <span style={s.count}>{t("deviceCount", { count: devices.length })}</span>
        </h2>
        <Button icon={Plus} onClick={() => setShowLinkModal(true)}>{t("linkDevice")}</Button>
      </div>

      {devices.length === 0 ? (
        <EmptyState icon={Link2} text={t("empty")} />
      ) : (
        <div style={s.list}>
          {devices.map((device) => {
            const assignment = device.CurrentAssignment;
            const busy = busyDeviceId === device.Id;

            return (
              <Card key={device.Id} style={s.card}>
                <div style={s.info}>
                  <div style={s.nameRow}>
                    <span style={s.name}>{device.DeviceName || t("unnamedDevice")}</span>
                    {assignment ? (
                      <Badge variant={assignment.IsBlocked ? "danger" : "primary"}>
                        {assignment.IsBlocked ? t("blocked") : (QR_TYPE_LABELS[assignment.QRType] || assignment.QRType)}
                      </Badge>
                    ) : (
                      <Badge variant="neutral">{t("notAssigned")}</Badge>
                    )}
                  </div>
                  <div style={s.meta}>
                    <span style={s.metaItem}><Fingerprint size={12} strokeWidth={2} /> {device.DeviceIdentifier}</span>
                    <span style={s.metaItem}><Clock size={12} strokeWidth={2} /> {t("lastSeen", { value: formatDateTime(device.LastSeenAt, t) })}</span>
                  </div>
                  {assignment && (
                    <div style={s.assignmentLine}><PartyPopper size={12} strokeWidth={2} /> {assignment.EventName}</div>
                  )}
                  {assignment?.IsBlocked && assignment.BlockReason && (
                    <div style={s.blockReason}>{t("blockReason", { reason: assignment.BlockReason })}</div>
                  )}
                </div>

                <div style={s.actions}>
                  <Button variant="secondary" disabled={busy} onClick={() => setAssignTarget(device)}>
                    {assignment ? t("actions.reassign") : t("actions.assign")}
                  </Button>
                  {assignment && (
                    <Button variant="secondary" disabled={busy} onClick={() => handleUnassign(device)}>{t("actions.unassign")}</Button>
                  )}
                  {assignment && !assignment.IsBlocked && (
                    <Button variant="danger-ghost" disabled={busy} onClick={() => { setBlockTarget(device); setBlockReason(""); }}>{t("actions.block")}</Button>
                  )}
                  {assignment?.QRType === "pQR" && !assignment.IsBlocked && canManageLocationExceptions && (
                    <Button variant="secondary" disabled={busy} onClick={() => { setExceptionTarget(device); setExceptionReason(""); setExceptionDuration("60"); setExceptionConfirmed(false); }}>{t("actions.locationException")}</Button>
                  )}
                  <Button variant="danger" disabled={busy} onClick={() => setDisconnectTarget(device)}>{t("actions.disconnect")}</Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {showLinkModal && (
        <LinkDeviceModal onClose={() => setShowLinkModal(false)} onLinked={loadDevices} />
      )}

      {assignTarget && (
        <AssignDeviceModal device={assignTarget} onClose={() => setAssignTarget(null)} onAssigned={loadDevices} />
      )}

      {disconnectTarget && (
        <ConfirmModal
          title={t("disconnectModal.title")}
          message={t("disconnectModal.message", { name: disconnectTarget.DeviceName || disconnectTarget.DeviceIdentifier })}
          confirmText={t("disconnectModal.confirm")}
          danger
          loading={busyDeviceId === disconnectTarget.Id}
          onConfirm={handleDisconnect}
          onCancel={() => setDisconnectTarget(null)}
        />
      )}


      {blockTarget && (
        <Modal
          title={t("blockModal.title")}
          subtitle={t("blockModal.subtitle", { name: blockTarget.DeviceName || blockTarget.DeviceIdentifier })}
          onClose={() => !busyDeviceId && setBlockTarget(null)}
          busy={busyDeviceId === blockTarget.Id}
          maxWidth={460}
          footer={(
            <>
              <Button variant="secondary" onClick={() => setBlockTarget(null)} disabled={busyDeviceId === blockTarget.Id}>{t("common:actions.cancel")}</Button>
              <Button variant="danger" onClick={handleBlock} disabled={busyDeviceId === blockTarget.Id || !blockReason.trim()}>{t("blockModal.confirm")}</Button>
            </>
          )}
        >
          <label htmlFor="device-block-reason" style={s.reasonLabel}>{t("blockModal.reasonLabel")}</label>
          <textarea id="device-block-reason" rows={4} maxLength={1000} value={blockReason} onChange={(event) => setBlockReason(event.target.value)} placeholder={t("blockModal.reasonPlaceholder")} style={s.reasonInput} autoFocus />
          <div style={s.reasonCount}>{blockReason.length}/1000</div>
        </Modal>
      )}

      {exceptionTarget && (
        <Modal title={t("exceptionModal.title")} subtitle={t("exceptionModal.subtitle")} onClose={() => !busyDeviceId && setExceptionTarget(null)} busy={busyDeviceId === exceptionTarget.Id} maxWidth={500}
          footer={<><Button variant="secondary" onClick={() => setExceptionTarget(null)} disabled={busyDeviceId === exceptionTarget.Id}>{t("common:actions.cancel")}</Button><Button variant="danger" onClick={handleLocationException} disabled={busyDeviceId === exceptionTarget.Id || !exceptionReason.trim() || !exceptionConfirmed}>{t("exceptionModal.confirmAction")}</Button></>}
        >
          <div style={s.warning}>{t("exceptionModal.warning")}</div>
          <label style={s.reasonLabel}>{t("exceptionModal.duration")}</label>
          <select className="input" value={exceptionDuration} onChange={(event) => setExceptionDuration(event.target.value)}>
            <option value="15">15 {t("exceptionModal.minutes")}</option><option value="60">60 {t("exceptionModal.minutes")}</option><option value="240">4 {t("exceptionModal.hours")}</option><option value="1440">24 {t("exceptionModal.hours")}</option>
          </select>
          <label htmlFor="location-exception-reason" style={{ ...s.reasonLabel, marginTop: 14 }}>{t("exceptionModal.reason")}</label>
          <textarea id="location-exception-reason" rows={4} maxLength={1000} value={exceptionReason} onChange={(event) => setExceptionReason(event.target.value)} style={s.reasonInput} />
          <label style={s.confirmRow}><input type="checkbox" checked={exceptionConfirmed} onChange={(event) => setExceptionConfirmed(event.target.checked)} /> {t("exceptionModal.confirmWarning")}</label>
        </Modal>
      )}
    </div>
  );
};

const s = {
  headerRow: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 },
  heading: { margin: 0, fontSize: 21, fontWeight: 800, color: color.textPrimary, display: "flex", alignItems: "center", gap: 10, letterSpacing: -0.4 },
  count: { fontSize: 12.5, fontWeight: 600, color: color.textMuted, background: color.gray100, padding: "3px 12px", borderRadius: 20 },
  list: { display: "flex", flexDirection: "column", gap: 12, maxWidth: 760 },
  card: { padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" },
  info: { flex: 1, minWidth: 240 },
  nameRow: { display: "flex", alignItems: "center", gap: 10, marginBottom: 7, flexWrap: "wrap" },
  name: { fontWeight: 700, fontSize: 15, color: color.textPrimary },
  meta: { display: "flex", gap: 14, fontSize: 12, color: color.textSecondary, flexWrap: "wrap" },
  metaItem: { display: "inline-flex", alignItems: "center", gap: 4 },
  assignmentLine: { display: "flex", alignItems: "center", gap: 5, marginTop: 6, fontSize: 13, color: color.gray700 },
  blockReason: { marginTop: 6, fontSize: 12, color: color.danger600 },
  reasonLabel: { display: "block", marginBottom: 6, fontWeight: 700, fontSize: 13, color: color.gray700 },
  reasonInput: { width: "100%", boxSizing: "border-box", resize: "vertical", border: `1px solid ${color.borderStrong}`, borderRadius: 8, padding: 10, font: "inherit" },
  reasonCount: { textAlign: "right", fontSize: 11, color: color.textMuted, marginTop: 5 },
  warning: { padding: 12, borderRadius: 8, background: color.warning50, color: color.gray700, fontSize: 12.5, lineHeight: 1.5, marginBottom: 14 },
  confirmRow: { display: "flex", alignItems: "flex-start", gap: 8, marginTop: 12, fontSize: 12.5, color: color.gray700, lineHeight: 1.45 },
  actions: { display: "flex", gap: 8, flexWrap: "wrap" },
};

export default LinkedDevices;
